import React, { useState, useEffect, useCallback } from 'react';
import { T, FONTS, SHADOW } from '../../constants/theme.js';
import { fetchPropertyAuditLogs } from '../../services/api.js';
import { Card, Btn, SearchInput, SectionHeader, Spinner, Empty, Badge } from '../../components/ui.jsx';

const ACTION_ICONS = {
  CHECK_IN:'✅', CHECK_OUT:'🚪', CANCEL_RESERVATION:'❌', NO_SHOW:'🚫',
  CREATE_STAFF:'👤', UPDATE_STAFF:'✏️', ACTIVATE_STAFF:'🟢', DEACTIVATE_STAFF:'🔴',
  CREATE_RESERVATION:'📋', CONFIRM_RESERVATION:'✔️', DELETE_RESERVATION:'🗑️',
  VIEW_RESERVATION:'👁️', VIEW_FOLIO:'🧾', CREATE_CHARGE:'💳', CREATE_PAYMENT:'💵',
  UPDATE_ROOM:'🏨', UPDATE_HOUSEKEEPING:'🧹', TOGGLE_STAFF_ACTIVE:'🔄',
  RESET_PASSWORD:'🔑', VIEW_GUEST:'👥', CREATE_GUEST:'👤', UPDATE_GUEST:'✏️',
  DEFAULT:'📝',
};

const METHOD_COLORS = {
  VIEW:   T.teal,
  CREATE: T.success,
  UPDATE: T.warning,
  DELETE: T.danger,
  CHECK:  T.gold,
};

function actionColor(action = '') {
  if (action.startsWith('VIEW'))   return METHOD_COLORS.VIEW;
  if (action.startsWith('CREATE')) return METHOD_COLORS.CREATE;
  if (action.startsWith('UPDATE')) return METHOD_COLORS.UPDATE;
  if (action.startsWith('DELETE')) return METHOD_COLORS.DELETE;
  if (action.startsWith('CHECK'))  return METHOD_COLORS.CHECK;
  return T.muted;
}

export default function AuditLogs({ property, onNavigate }) {
  const [logs,        setLogs]        = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [page,        setPage]        = useState(0);
  const [hasMore,     setHasMore]     = useState(false);
  const [search,      setSearch]      = useState('');
  const [filterAction,setFilterAction]= useState('');
  const [filterStaff, setFilterStaff] = useState('');

  const pid = property?.id;

  const load = useCallback(async (pg = 0) => {
    if (!pid) return;
    if (pg === 0) setLoading(true);
    try {
      const data = await fetchPropertyAuditLogs(pid, pg);
      const items = Array.isArray(data)
        ? data
        : (data?.content || []);
      if (pg === 0) {
        setLogs(items);
      } else {
        setLogs(prev => [...prev, ...items]);
      }
      const total = data?.totalElements ?? items.length;
      setHasMore(items.length === 50 && (pg + 1) * 50 < total);
      setPage(pg);
    } catch (e) {
      setLogs([]);
    } finally {
      setLoading(false);
    }
  }, [pid]);

  useEffect(() => { load(0); }, [load]);

  const refresh = () => load(0);

  // Client-side filtering
  const filtered = logs.filter(l => {
    const q = search.toLowerCase();
    const matchSearch = !q ||
      (l.staffName || '').toLowerCase().includes(q) ||
      (l.action    || '').toLowerCase().includes(q) ||
      (l.details   || '').toLowerCase().includes(q) ||
      (l.entityType|| '').toLowerCase().includes(q);
    const matchAction = !filterAction || (l.action || '').startsWith(filterAction);
    const matchStaff  = !filterStaff  || (l.staffId || l.staffName || '') === filterStaff;
    return matchSearch && matchAction && matchStaff;
  });

  // Unique staff list for filter
  const staffList = [...new Set(logs.map(l => l.staffName).filter(Boolean))].sort();

  return (
    <div className="fade-up">
      <SectionHeader
        title="Audit Logs"
        subtitle={`${property?.name} — complete activity trail`}
        right={<Btn variant="secondary" size="sm" onClick={refresh}>↻ Refresh</Btn>}
      />

      {/* Filter bar */}
      <div style={{ display:'flex', gap:10, marginBottom:16, flexWrap:'wrap', alignItems:'center' }}>
        <div style={{ flex:1, minWidth:220 }}>
          <SearchInput value={search} onChange={setSearch} placeholder="Search actions, staff, details…" />
        </div>
        <select value={filterAction} onChange={e => setFilterAction(e.target.value)}
          style={{ border:`1px solid ${T.border}`, borderRadius:9, padding:'8px 12px',
            fontSize:12, color:T.navy, background:'#fff', fontFamily:FONTS.body, minWidth:140 }}>
          <option value="">All action types</option>
          <option value="VIEW">Views (reads)</option>
          <option value="CREATE">Creates</option>
          <option value="UPDATE">Updates</option>
          <option value="DELETE">Deletes</option>
          <option value="CHECK">Check-in / out</option>
        </select>
        <select value={filterStaff} onChange={e => setFilterStaff(e.target.value)}
          style={{ border:`1px solid ${T.border}`, borderRadius:9, padding:'8px 12px',
            fontSize:12, color:T.navy, background:'#fff', fontFamily:FONTS.body, minWidth:160 }}>
          <option value="">All staff</option>
          {staffList.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        {(search || filterAction || filterStaff) && (
          <Btn size="sm" variant="ghost" onClick={() => { setSearch(''); setFilterAction(''); setFilterStaff(''); }}>
            ✕ Clear
          </Btn>
        )}
        <span style={{ fontSize:11, color:T.muted, whiteSpace:'nowrap' }}>
          {filtered.length} of {logs.length} entries
        </span>
      </div>

      {loading ? (
        <div style={{ padding:60, textAlign:'center' }}><Spinner size={40} /></div>
      ) : filtered.length === 0 ? (
        <Empty icon="📭" title="No logs found"
          subtitle={search ? 'Try a different search term' : 'Activity will appear here as staff use the system'} />
      ) : (
        <Card style={{ padding:0, overflow:'hidden' }}>
          <div style={{ overflowX:'auto' }}>
            <table style={{ width:'100%', borderCollapse:'collapse', minWidth:640 }}>
              <thead>
                <tr style={{ background:T.surface }}>
                  {['Time','Staff','Action','Entity','Details'].map(h => (
                    <th key={h} style={{ padding:'10px 16px', textAlign:'left', fontSize:10,
                      fontWeight:700, color:T.muted, letterSpacing:.8, textTransform:'uppercase',
                      borderBottom:`1px solid ${T.border}`, whiteSpace:'nowrap' }}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((entry, i) => {
                  const icon  = ACTION_ICONS[entry.action] || ACTION_ICONS.DEFAULT;
                  const color = actionColor(entry.action || '');
                  const dt    = entry.performedAt ? new Date(entry.performedAt) : null;
                  const dateStr = dt ? dt.toLocaleDateString('en-GB', { day:'2-digit', month:'short' }) : '';
                  const timeStr = dt ? dt.toLocaleTimeString('en-GB', { hour:'2-digit', minute:'2-digit' }) : '';

                  return (
                    <tr key={i}
                      style={{ background: i % 2 === 0 ? '#fff' : T.surface }}
                      onMouseEnter={e => e.currentTarget.style.background = `${T.teal}08`}
                      onMouseLeave={e => e.currentTarget.style.background = i%2===0?'#fff':T.surface}>
                      {/* Time */}
                      <td style={{ padding:'10px 16px', whiteSpace:'nowrap', borderBottom:`1px solid ${T.border}` }}>
                        <p style={{ fontSize:12, fontWeight:600, color:T.navy }}>{timeStr}</p>
                        <p style={{ fontSize:10, color:T.muted }}>{dateStr}</p>
                      </td>
                      {/* Staff */}
                      <td style={{ padding:'10px 16px', borderBottom:`1px solid ${T.border}` }}>
                        <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                          <div style={{ width:28, height:28, borderRadius:'50%',
                            background:`${T.teal}20`, display:'flex', alignItems:'center',
                            justifyContent:'center', fontSize:11, fontWeight:700, color:T.teal, flexShrink:0 }}>
                            {(entry.staffName||'?')[0].toUpperCase()}
                          </div>
                          <div>
                            <p style={{ fontSize:12, fontWeight:600, color:T.navy, whiteSpace:'nowrap' }}>
                              {entry.staffName || 'System'}
                            </p>
                            {entry.staffRole && (
                              <p style={{ fontSize:10, color:T.muted, textTransform:'uppercase', letterSpacing:.3 }}>
                                {entry.staffRole}
                              </p>
                            )}
                          </div>
                        </div>
                      </td>
                      {/* Action */}
                      <td style={{ padding:'10px 16px', borderBottom:`1px solid ${T.border}`, whiteSpace:'nowrap' }}>
                        <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                          <span style={{ fontSize:14 }}>{icon}</span>
                          <span style={{ fontSize:11, fontWeight:700, color,
                            textTransform:'uppercase', letterSpacing:.3 }}>
                            {(entry.action || '').replace(/_/g,' ')}
                          </span>
                        </div>
                      </td>
                      {/* Entity */}
                      <td style={{ padding:'10px 16px', borderBottom:`1px solid ${T.border}` }}>
                        {entry.entityType && (
                          <span style={{ fontSize:11, color:T.muted, background:T.surface,
                            border:`1px solid ${T.border}`, borderRadius:99, padding:'2px 8px' }}>
                            {entry.entityType}
                          </span>
                        )}
                      </td>
                      {/* Details */}
                      <td style={{ padding:'10px 16px', borderBottom:`1px solid ${T.border}`,
                        maxWidth:280, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                        <span style={{ fontSize:11, color:T.slate }} title={entry.details || ''}>
                          {entry.details || '—'}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {hasMore && (
            <div style={{ padding:'14px 20px', textAlign:'center', borderTop:`1px solid ${T.border}` }}>
              <Btn variant="secondary" onClick={() => load(page + 1)}>Load more</Btn>
            </div>
          )}
        </Card>
      )}
    </div>
  );
}
